import { LocalStorageSync } from '@vagabondinternet/utilities';
import React, { useCallback, useContext, useMemo, useState } from 'react';

const USER_STATE_KEY = 'console_dashboard';

export interface UserProviderProps {
  children: React.ReactNode;
}

type UserState = {
  leftMenuCollapsed: boolean;
};

type ClearUserStateCallback = () => void;
type SetUserStateCallback = <K extends keyof UserState>(key: K, value: UserState[K]) => void;

const defaultUserState: UserState = {
  leftMenuCollapsed: false,
};

export const UserStateContext = React.createContext<
  UserState & { clear: ClearUserStateCallback; setUserState: SetUserStateCallback }
>({
  ...defaultUserState,
  clear: () => {
    /* noop */
  },
  setUserState: () => {
    /* noop */
  },
});

export const UserState: React.FC<UserProviderProps> = ({ children }: UserProviderProps) => {
  const [localUserState, setLocalUserState] = useState<UserState>(
    LocalStorageSync.getObject(USER_STATE_KEY, {
      suppressError: true,
      defaultValue: defaultUserState,
    }),
  );

  const clear = useCallback(() => {
    LocalStorageSync.deleteObject(USER_STATE_KEY);
  }, []);

  const setUserState = useCallback<SetUserStateCallback>(
    (key, value) => {
      const nextUserState = {
        ...localUserState,
        [key]: value,
      };

      setLocalUserState(nextUserState);
      LocalStorageSync.setObject(USER_STATE_KEY, nextUserState, { suppressError: true, defaultValue: undefined });
    },
    [localUserState],
  );

  const userState = useMemo(
    () => ({
      ...localUserState,
      clear,
      setUserState,
    }),
    [clear, localUserState, setUserState],
  );

  return <UserStateContext.Provider value={userState}>{children}</UserStateContext.Provider>;
};

export const useUserState = () => {
  const userState = useContext(UserStateContext);
  return userState;
};
